import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Stack, Typography} from 'views/components';
import {Translate} from 'views/module';

import DomainGuideAssignment from './domain_guide_scenes/DomainGuideAssignment';
import DomainGuideDetails from './domain_guide_scenes/DomainGuideDetails';
import DomainGuideMarking from './domain_guide_scenes/DomainGuideMarking';
import DomainGuideOutreachHours from './domain_guide_scenes/DomainGuideOutreachHours';
import DomainGuideRubric from './domain_guide_scenes/DomainGuideRubric';
import RawDomainGuide from './domain_guide_scenes/RawDomainGuide';

import {formatCreatedByLabel, isUserRn} from '../utils/createdBy';

import backend from 'utils/backend';
import featureFlags from 'utils/feature_flags/feature_flags';

const DomainGuideView = (props) => {
  const {
    domainGuideRn,
    statusProgressWrap: progressWrap,
  } = props;
  const [domainGuideData, setDomainGuideData] = React.useState(null);
  const [domainGuideDataLoaded, setDomainGuideDataLoaded] = React.useState(false);
  const [createdByLabel, setCreatedByLabel] = React.useState('');

  React.useEffect(() => {
    let isActive = true;

    const loadDomainGuide = async () => {
      await progressWrap(async () => {
        const domainGuideResponse = await backend.domainGuide.getDomainGuide(domainGuideRn);
        const domainGuide = domainGuideResponse?.domainGuide || domainGuideResponse || null;
        let nextCreatedByLabel = domainGuide?.createdBy || '';

        if (isUserRn(domainGuide?.createdBy)) {
          try {
            const userResponse = await backend.user.getUser(domainGuide.createdBy, false);

            nextCreatedByLabel = formatCreatedByLabel(userResponse?.user, domainGuide.createdBy);
          } catch {
            nextCreatedByLabel = domainGuide.createdBy;
          }
        }

        if (isActive) {
          setDomainGuideData(domainGuide);
          setCreatedByLabel(nextCreatedByLabel);
          setDomainGuideDataLoaded(true);
        }
      });
    };

    loadDomainGuide();

    return () => {
      isActive = false;
    };
  }, [domainGuideRn, progressWrap]);

  if (!domainGuideDataLoaded) {
    return null;
  }

  if (!domainGuideData) {
    return (
      <Box
        sx={{
          textAlign: 'left',
          width: {
            xs: '100%',
            md: '75%',
          },
        }}
      >
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            color: '#1A1916',
          }}
        >
          <Translate expression='domainGuide.Detail.NotFound' />
        </Typography>
      </Box>
    );
  }

  const updateStatus = (status) => {
    if (!domainGuideData.domainGuideRn || status === domainGuideData.status) {
      return;
    }

    progressWrap(async () => {
      const statusResponse = await backend.domainGuide.updateStatus(domainGuideData.domainGuideRn, status);
      const updatedDomainGuide = statusResponse?.domainGuide || statusResponse || {};

      setDomainGuideData((prevDomainGuideData) => ({
        ...prevDomainGuideData,
        status: updatedDomainGuide.status || status,
      }));
    });
  };

  const updateTeamName = (teamName) => {
    if (!domainGuideData.domainGuideRn || teamName === domainGuideData.teamName) {
      return;
    }

    progressWrap(async () => {
      const teamNameResponse = await backend.domainGuide.updateTeamName(domainGuideData.domainGuideRn, teamName);
      const updatedDomainGuide = teamNameResponse?.domainGuide || teamNameResponse || {};

      setDomainGuideData((prevDomainGuideData) => ({
        ...prevDomainGuideData,
        ...updatedDomainGuide,
        teamName: updatedDomainGuide.teamName || teamName,
      }));
    });
  };

  const updateOutreachHours = async (outreachHours) => {
    if (!domainGuideData.domainGuideRn) {
      return false;
    }

    let wasUpdated = false;

    await progressWrap(async () => {
      const outreachHoursResponse = await backend.domainGuide.updateOutreachHours(
          domainGuideData.domainGuideRn,
          outreachHours,
      );
      const updatedDomainGuide = outreachHoursResponse?.domainGuide || outreachHoursResponse || {};

      setDomainGuideData((prevDomainGuideData) => ({
        ...prevDomainGuideData,
        outreachHours: updatedDomainGuide.outreachHours || outreachHours,
      }));
      wasUpdated = true;
    });

    return wasUpdated;
  };

  const updateRubric = async (rubric) => {
    if (!domainGuideData.domainGuideRn) {
      return false;
    }

    let saved = false;

    await progressWrap(async () => {
      const rubricResponse = await backend.domainGuide.updateRubric(domainGuideData.domainGuideRn, rubric);
      const updatedDomainGuide = rubricResponse?.domainGuide || {};

      setDomainGuideData((prevDomainGuideData) => ({
        ...prevDomainGuideData,
        rubric: updatedDomainGuide.rubric || rubric,
        rubricUpdatedAt: updatedDomainGuide.rubricUpdatedAt || prevDomainGuideData.rubricUpdatedAt,
      }));
      saved = true;
    });

    return saved;
  };

  const updateMarking = async (marking) => {
    if (!domainGuideData.domainGuideRn) {
      return false;
    }

    let saved = false;

    await progressWrap(async () => {
      const markingResponse = await backend.domainGuide.updateMarking(domainGuideData.domainGuideRn, marking);
      const updatedDomainGuide = markingResponse?.domainGuide || {};

      setDomainGuideData((prevDomainGuideData) => ({
        ...prevDomainGuideData,
        marking: updatedDomainGuide.marking || marking,
      }));
      saved = true;
    });

    return saved;
  };

  const updateSlackIds = (slackIds) => {
    if (!domainGuideData.domainGuideRn || !Array.isArray(slackIds)) {
      return;
    }

    progressWrap(async () => {
      const slackIdResponse = await backend.domainGuide.updateSlackIds(domainGuideData.domainGuideRn, slackIds);
      const updatedDomainGuide = slackIdResponse?.domainGuide || slackIdResponse || {};

      setDomainGuideData((prevDomainGuideData) => ({
        ...prevDomainGuideData,
        ...updatedDomainGuide,
        slackIds: Array.isArray(updatedDomainGuide.slackIds) ?
          updatedDomainGuide.slackIds :
          slackIds,
      }));
    });
  };

  return (
    <Box
      sx={{
        textAlign: 'left',
        width: {
          xs: '100%',
          md: '75%',
        },
      }}
    >
      <Stack gap={3}>
        <DomainGuideDetails
          createdByLabel={createdByLabel}
          domainGuideData={domainGuideData}
          updateStatus={updateStatus}
        />
        <DomainGuideAssignment
          domainGuideData={domainGuideData}
          updateSlackIds={updateSlackIds}
          updateTeamName={updateTeamName}
        />
        <DomainGuideOutreachHours
          domainGuideData={domainGuideData}
          updateOutreachHours={updateOutreachHours}
        />
        <DomainGuideRubric
          domainGuideData={domainGuideData}
          updateRubric={updateRubric}
        />
        {
          // PUT /domain_guide/{rn}/marking only exists once engine PR2 is
          // deployed.
          featureFlags.oversightMarkingControlsEnabled &&
          <DomainGuideMarking
            domainGuideData={domainGuideData}
            updateMarking={updateMarking}
          />
        }
        <RawDomainGuide rawDomainGuide={domainGuideData.rawDomainGuide} />
      </Stack>
    </Box>
  );
};

DomainGuideView.propTypes = {
  domainGuideRn: PropTypes.string.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(DomainGuideView));
